import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { AlertCircle, Bell, CheckCircle, Eye, EyeOff, Loader2, LogIn, LogOut, Mail, User } from 'lucide-react'
import { GoogleAuthProvider, createUserWithEmailAndPassword, fetchSignInMethodsForEmail, sendPasswordResetEmail, signInWithEmailAndPassword, signInWithPopup, signOut } from 'firebase/auth'
import { auth } from '../firebase-config'

const inputClassName = 'w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all'

function toAuthMessage(err, isNewUser = false) {
  const code = String(err?.code || '')
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
    return 'Inicio con Google cancelado.'
  }
  if (code === 'auth/invalid-credential') return 'Email o contraseña incorrectos.'
  if (code === 'auth/user-not-found') return 'No existe una cuenta con ese email.'
  if (code === 'auth/wrong-password') return 'Contraseña incorrecta.'
  if (code === 'auth/email-already-in-use') return 'Ese email ya está registrado. Desactiva "primer acceso" e inicia sesión.'
  if (code === 'auth/weak-password') return 'La contraseña es demasiado débil.'
  if (code === 'auth/invalid-email') return 'Email inválido.'
  if (code === 'auth/too-many-requests') return 'Demasiados intentos. Espera unos minutos e intenta nuevamente.'
  if (code === 'auth/operation-not-allowed') return isNewUser ? 'Registro por email deshabilitado en Firebase Auth.' : 'Login por email deshabilitado en Firebase Auth.'
  return err?.message || 'No se pudo autenticar.'
}

function toPasswordSetupMessage(err) {
  const code = String(err?.code || '')
  const rawMessage = String(err?.message || '')
  const upstream = String(err?.customData?._tokenResponse?.error?.message || '')
  const combined = `${rawMessage} ${upstream}`.toUpperCase()
  if (code === 'auth/weak-password') return 'La contraseña debe ser más robusta (mínimo 8 caracteres).'
  if (code === 'auth/requires-recent-login') return 'Por seguridad, vuelve a iniciar sesión y reintenta definir la contraseña.'
  if (code === 'auth/operation-not-allowed') return 'El acceso por email/contraseña no está habilitado en Firebase Auth.'
  if (code === 'auth/network-request-failed') return 'Error de red al actualizar la contraseña. Intenta nuevamente.'
  if (combined.includes('PASSWORD_LOGIN_DISABLED') || combined.includes('OPERATION_NOT_ALLOWED')) {
    return 'Firebase no permite login por email/contraseña en este proyecto. Debes habilitarlo en Authentication > Sign-in method.'
  }
  if (combined.includes('WEAK_PASSWORD')) return 'La contraseña es débil. Usa al menos 8 caracteres combinando letras y números.'
  if (combined.includes('CREDENTIAL_TOO_OLD_LOGIN_AGAIN')) return 'Debes revalidar tu cuenta Google e intentarlo nuevamente.'
  if (combined.includes('INVALID_ID_TOKEN')) return 'Sesión inválida. Cierra sesión e ingresa nuevamente con Google.'
  if (code) return `No se pudo actualizar la contraseña (${code}).`
  return err?.message || 'No se pudo actualizar la contraseña.'
}

export default function UserLogin({
  onLoginSuccess,
  onLogout,
  isAuthenticated,
  userCuit,
  userDisplayName = '',
  isAdmin = false,
  forcePasswordSetup = false,
  forcePasswordChange = false,
  onGoToAdmin,
  coinsBalance = null,
  openTickets = 0,
  hasAdminUpdates = false,
  coinsLoading = false,
  onBuyMore,
  onOpenSupport,
  onOpenTickets,
  onPasswordSetupResolved,
}) {
  const [emailInput, setEmailInput] = useState('')
  const [passwordInput, setPasswordInput] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isNewUser, setIsNewUser] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [requirePasswordChange, setRequirePasswordChange] = useState(false)
  const [requirePasswordSetup, setRequirePasswordSetup] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [passwordSetupCompleted, setPasswordSetupCompleted] = useState(false)
  const mustSetPassword = !passwordSetupCompleted && Boolean(requirePasswordSetup || requirePasswordChange || forcePasswordSetup || forcePasswordChange)

  const startGoogleLogin = async (loginHint = '') => {
    const provider = new GoogleAuthProvider()
    if (loginHint) provider.setCustomParameters({ login_hint: loginHint, prompt: 'select_account' })
    await signInWithPopup(auth, provider)
    const idToken = await auth.currentUser.getIdToken(true)
    const response = await fetch('/api/user/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken }),
    })
    const data = await response.json()
    if (!data.success) throw new Error(data.error || 'No se pudo iniciar sesión con Google')
    localStorage.setItem('userToken', idToken)
    localStorage.setItem('userUid', auth.currentUser?.uid || '')
    onLoginSuccess(data.userRef || data.uid || auth.currentUser?.email || '', idToken)
    setRequirePasswordChange(Boolean(data.user?.forcePasswordChange))
    setRequirePasswordSetup(Boolean(data.user?.forcePasswordSetup))
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    const email = String(emailInput || '').trim().toLowerCase()
    if (!email || !email.includes('@')) {
      setError('Email inválido')
      return
    }
    if (!passwordInput || passwordInput.length < 6) {
      setError('La contraseña debe tener mínimo 6 caracteres')
      return
    }

    try {
      setIsLoading(true)
      if (!isNewUser) {
        const methods = await fetchSignInMethodsForEmail(auth, email)
        const hasPassword = methods.includes('password')
        const hasGoogle = methods.includes('google.com')
        if (!hasPassword && hasGoogle) {
          setError('Esta cuenta está vinculada a Google. Usa el botón "Continuar con Google".')
          return
        }
      }
      if (isNewUser) {
        await createUserWithEmailAndPassword(auth, email, passwordInput)
      } else {
        await signInWithEmailAndPassword(auth, email, passwordInput)
      }
      const idToken = await auth.currentUser.getIdToken(true)
      const response = await fetch('/api/user/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken }),
      })
      const data = await response.json()
      if (!data.success) throw new Error(data.error || 'No se pudo iniciar sesión')

      localStorage.setItem('userToken', idToken)
      localStorage.setItem('userUid', auth.currentUser?.uid || '')
      setEmailInput('')
      setPasswordInput('')
      setIsNewUser(false)
      onLoginSuccess(data.userRef || data.uid || email, idToken)
      setRequirePasswordChange(Boolean(data.user?.forcePasswordChange))
      setRequirePasswordSetup(Boolean(data.user?.forcePasswordSetup))
      setSuccess('Sesión iniciada exitosamente')
    } catch (err) {
      setError(toAuthMessage(err, isNewUser))
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('userToken')
    localStorage.removeItem('userRef')
    localStorage.removeItem('userUid')
    signOut(auth).catch(() => {})
    onLogout()
  }

  const handleForgotPassword = async (e) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailInput || !emailRegex.test(emailInput)) {
      setError('Email inválido')
      return
    }
    try {
      setIsLoading(true)
      await sendPasswordResetEmail(auth, emailInput)
      setSuccess('Email de recuperación enviado. Revisa tu bandeja de entrada.')
      setEmailInput('')
      setTimeout(() => {
        setShowForgotPassword(false)
        setSuccess(null)
      }, 3000)
    } catch {
      setError('Error enviando email')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    setError(null)
    setSuccess(null)
    try {
      setIsLoading(true)
      await startGoogleLogin(String(emailInput || '').trim().toLowerCase())
    } catch (err) {
      setError(toAuthMessage(err, false))
    } finally {
      setIsLoading(false)
    }
  }

  const handleChangeInitialPassword = async () => {
    try {
      if (!newPassword || newPassword.length < 8) {
        setError('La nueva contraseña debe tener al menos 8 caracteres')
        return
      }
      if (newPassword !== confirmPassword) {
        setError('Las contraseñas no coinciden. Verifica ambos campos.')
        return
      }
      setIsLoading(true)
      const idToken = await auth.currentUser.getIdToken(true)
      const response = await fetch('/api/user/set-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken, newPassword }),
      })
      const data = await response.json()
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'No se pudo actualizar la contraseña')
      }
      setRequirePasswordChange(false)
      setRequirePasswordSetup(false)
      setPasswordSetupCompleted(true)
      setNewPassword('')
      setConfirmPassword('')
      setSuccess('Contraseña actualizada. Ya puedes usar el sistema normalmente.')
      if (onPasswordSetupResolved) onPasswordSetupResolved()
    } catch (err) {
      setError(toPasswordSetupMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  if (!isAuthenticated) {
    return (
      <div className="user-login-card">
        <div className="mb-4 flex items-center gap-3">
          <div className="rounded-full border border-white/20 bg-white/10 p-2">
            <User className="h-5 w-5 text-cyan-200" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-white">{showForgotPassword ? 'Recuperar acceso' : isNewUser ? 'Crear cuenta' : 'Entrar al sistema'}</h2>
            <p className="text-xs text-slate-300">Acceso seguro</p>
          </div>
        </div>

        {error && <div className="mb-3 flex gap-2 rounded-xl border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-100"><AlertCircle className="h-4 w-4 mt-0.5" />{error}</div>}
        {success && <div className="mb-3 flex gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3 text-sm text-emerald-100"><CheckCircle className="h-4 w-4 mt-0.5" />{success}</div>}

        {showForgotPassword ? (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <label className="mb-2 block text-sm text-slate-200">Email asociado</label>
              <input type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} placeholder="tu@email.com" className={inputClassName} disabled={isLoading} />
            </div>
            <button type="submit" disabled={isLoading} className="w-full rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 py-2.5 font-medium text-white transition hover:brightness-110 disabled:opacity-60">Enviar recuperación</button>
            <button type="button" onClick={() => { setShowForgotPassword(false); setError(null); setSuccess(null) }} className="w-full text-sm text-cyan-200 hover:text-cyan-100">Volver al login</button>
          </form>
        ) : (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
                <label className="mb-2 block text-sm text-slate-200">Email</label>
                <input type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} placeholder="tu@email.com" className={inputClassName} disabled={isLoading} />
              </div>

            <div>
              <label className="mb-2 block text-sm text-slate-200">{isNewUser ? 'Crear contraseña' : 'Contraseña'}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  placeholder={isNewUser ? 'Mínimo 6 caracteres' : '••••••'}
                  className={`${inputClassName} pr-10`}
                  disabled={isLoading}
                  minLength="6"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-200">
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input type="checkbox" id="firstAccess" checked={isNewUser} onChange={(e) => setIsNewUser(e.target.checked)} className="h-4 w-4 rounded border-white/20 bg-white/10" disabled={isLoading} />
              <label htmlFor="firstAccess" className="text-sm text-slate-200">Es mi primer acceso (crear cuenta)</label>
            </div>

            <motion.button
              type="submit"
              disabled={isLoading}
              whileTap={{ scale: 0.98 }}
              animate={isLoading ? { scale: [1, 1.02, 1] } : { scale: 1 }}
              transition={isLoading ? { repeat: Infinity, duration: 1.2, ease: 'easeInOut' } : { duration: 0.2 }}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-violet-500 py-2.5 font-semibold text-white shadow-[0_10px_24px_rgba(59,130,246,0.35)] transition hover:brightness-110 disabled:opacity-70"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  {isNewUser ? 'Creando cuenta...' : 'Iniciando sesión...'}
                </>
              ) : (
                <>
                  <LogIn className="h-5 w-5" />
                  {isNewUser ? 'Crear cuenta' : 'Entrar'}
                </>
              )}
            </motion.button>
            <button type="button" onClick={handleGoogleLogin} disabled={isLoading} className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/10 py-2.5 text-sm font-semibold text-white hover:bg-white/15 disabled:opacity-60">
              <Mail className="h-4 w-4" /> Continuar con Google
            </button>

            {!isNewUser && (
              <button type="button" onClick={() => setShowForgotPassword(true)} className="w-full text-sm text-cyan-200 hover:text-cyan-100">
                Olvidé mi contraseña
              </button>
            )}
          </form>
        )}
      </div>
    )
  }

  return (
    <div className="user-login-card user-panel rounded-2xl border border-white/20 bg-white/10 p-4 text-white backdrop-blur-xl">
      {error && <div className="mb-3 flex gap-2 rounded-xl border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-100"><AlertCircle className="mt-0.5 h-4 w-4" />{error}</div>}
      {success && <div className="mb-3 flex gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3 text-sm text-emerald-100"><CheckCircle className="mt-0.5 h-4 w-4" />{success}</div>}

      {mustSetPassword && (
        <div className="rounded-xl border border-amber-300/40 bg-amber-500/20 p-3 text-xs text-amber-100">
          <p className="font-semibold">Debes definir una contraseña para continuar</p>
          <p className="mt-1">Esta acción es obligatoria y se solicita una sola vez para habilitar ingreso por email + contraseña.</p>
          <div className="mt-2 flex gap-2">
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Nueva contraseña" className={inputClassName} />
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirmar contraseña" className={inputClassName} />
            <button type="button" onClick={handleChangeInitialPassword} disabled={isLoading} className="btn-profile-accent rounded-lg px-3 py-2 text-xs font-semibold">{isLoading ? 'Guardando...' : 'Guardar'}</button>
          </div>
        </div>
      )}

      {!mustSetPassword && (
        <>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="rounded-full border border-white/20 bg-white/10 p-2.5 shrink-0">
            <User className="h-5 w-5 text-cyan-100" />
          </div>
          <div className="min-w-0">
            <p className="user-panel-label text-xs text-slate-300">Conectado como</p>
            <p className="user-panel-value truncate text-lg font-semibold text-white">{userDisplayName || userCuit || 'Usuario'}</p>
            <p className="user-panel-label truncate text-[11px] text-slate-300">{userCuit || auth?.currentUser?.email || '-'}</p>
          </div>
        </div>
        <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 rounded-xl border border-red-400/40 bg-red-500/20 px-3 py-2 text-sm text-red-100 hover:bg-red-500/30">
          <LogOut className="h-4 w-4" /> Cerrar
        </button>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3">
        <div className="user-panel-metric rounded-xl border border-white/20 bg-white/5 px-3 py-2">
          <p className="user-panel-label text-[11px] uppercase tracking-wide text-slate-300">Usuario</p>
          <p className="user-panel-value truncate text-sm font-semibold text-white">{userDisplayName || userCuit || 'Usuario'}</p>
        </div>
        <div className="user-panel-metric rounded-xl border border-white/20 bg-white/5 px-3 py-2">
          <p className="user-panel-label text-[11px] uppercase tracking-wide text-slate-300">Coins</p>
          <p className="user-panel-value text-sm font-semibold text-white">{coinsBalance ?? 0}</p>
        </div>
        <button
          type="button"
          onClick={() => onOpenTickets && onOpenTickets()}
          className="rounded-xl border border-white/20 bg-white/5 px-3 py-2 text-left hover:bg-white/10"
        >
          <p className="text-[11px] uppercase tracking-wide text-slate-300">Tickets abiertos</p>
          <p className="flex items-center gap-2 text-sm font-semibold text-white">
            {openTickets}
            {hasAdminUpdates && <Bell className="h-4 w-4 text-amber-300" />}
          </p>
        </button>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-2">
        <button
          type="button"
          onClick={() => onBuyMore && onBuyMore()}
          disabled={coinsLoading}
          className="btn-profile-accent rounded-xl px-4 py-2 text-sm font-semibold disabled:opacity-60"
        >
          {coinsLoading ? 'Actualizando...' : '+ Comprar coins'}
        </button>
        <button
          type="button"
          onClick={() => onOpenSupport && onOpenSupport()}
          className="btn-profile-accent rounded-xl px-4 py-2 text-sm font-semibold"
        >
          Abrir ticket de soporte
        </button>
      </div>

      {isAdmin && (
        <div className="mt-4 border-t border-white/15 pt-4">
          <button type="button" onClick={() => onGoToAdmin && onGoToAdmin()} className="btn-profile-accent w-full rounded-xl px-4 py-2 text-sm font-semibold">
            Ir a panel administrador
          </button>
        </div>
      )}

      <div className="mt-4 border-t border-white/15 pt-4">
        <a
          href="/MANUAL_DE_USUARIO.md"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-profile-accent flex w-full items-center justify-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-2.5 text-sm font-semibold text-cyan-100 hover:bg-cyan-500/20"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-book-open"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
          Manual de Usuario
        </a>
      </div>
        </>
      )}
    </div>
  )
}
